import { difference } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { StoreState } from '~/reducer';
import { userSettingsStoreStateSelector } from '~/selectors';

import { requireUserSettings } from './slice';
import { useUserSettingsBooleanFlagSelector } from './useUserSettingsBooleanFlagSelector';

export interface UseLoadRemoteUserSettingsOutput {
  isLoading: boolean;
  failedToLoad: boolean;
}

export const useLoadRemoteUserSettings = (
  keys?: string[],
): UseLoadRemoteUserSettingsOutput => {
  const dispatch = useDispatch();

  const isLoading = useUserSettingsBooleanFlagSelector('isLoading', keys);
  const failedToLoad = useUserSettingsBooleanFlagSelector('failedToLoad', keys);

  const requestedUserSettings = useSelector((state: StoreState) => {
    return userSettingsStoreStateSelector(state).requestedUserSettings;
  });

  const didNotRequestUserSettings = useMemo(() => {
    return keys && requestedUserSettings
      ? !!difference(keys, requestedUserSettings).length
      : !keys && !!requestedUserSettings;
  }, [requestedUserSettings, keys]);

  useEffect(() => {
    dispatch(requireUserSettings(keys));
  }, [dispatch, keys]);

  return {
    /**
     * To avoid passing isLoading false and after that true,
     * consider isLoading true while user settings were
     * not requested.
     */
    isLoading: isLoading || didNotRequestUserSettings,
    failedToLoad,
  };
};
